import type { TurnProgressStage } from '@salescore/agent-contract'

import type { ApprovalAnswer, ApprovalRequest, ChatEvent, ToolUpdate, TurnEndStatus } from './chatEvents'
import type { TurnWaiting } from './turnProgress'

// セッションごとの会話と状態遷移。memo 化した行の再描画を抑えるため、変わらない行の参照を保つ。

// 承認のカードの結末。答え（承認、却下）と、答えないままの終わり（cancelled）に加えて、
// このタブで送った答えを API が受け付けなかったこと（notAccepted）を持つ
export type ApprovalOutcome = ApprovalAnswer | 'notAccepted'

export type ChatItem =
  | { kind: 'user'; id: string; turnId: string; text: string }
  // 1ターンの回答は、ツールの呼び出しの前後で別の行に分ける。messageId は Codex の agentMessage の item の ID（chatEvents.ts）
  | { kind: 'assistant'; id: string; turnId: string; text: string; isStreaming: boolean; messageId: string | null }
  | ({ kind: 'tool'; turnId: string } & ToolUpdate)
  // ツールの実行の前の承認のカード。id は承認の ID。answer は、結末が決まるまで null。
  // isAnswering は、このタブで答えを送り、API の応答を待っている間だけ true。カードの部品ではなく会話に持つのは、
  // ウィジェットを閉じて開き直しても、その間に別の答えを送らせないためである（runtime は最初の答えだけを受け付ける）
  | ({ kind: 'approval'; turnId: string; answer: ApprovalOutcome | null; isAnswering: boolean } & ApprovalRequest)
  // 完了以外（中断、失敗）で終わったターンの終わりの行
  | { kind: 'turnEnd'; id: string; turnId: string; status: Exclude<TurnEndStatus, 'completed'>; message: string | null }

export interface SessionChat {
  items: readonly ChatItem[]
  // 実行中のターン。なければ null
  runningTurnId: string | null
  // エージェントが回答を終えたターン。runtime はこの後にタイトルの作成などをしてから completed を送るので、
  // その間は実行中のままでも、回答の後に「考え中」を出さないために使う
  generationCompletedTurnId: string | null
  // 実行中のターンで、runtime の progress で最後に届いた段階。「考え中」の欄の文言に使う。届いていなければ null
  runningTurnStage: TurnProgressStage | null
  // 実行中のターンをこのタブで送り、runtime の最初のイベントを待っている間だけ持つ（turnProgress.ts）
  runningTurnWaiting: TurnWaiting | null
  // 停止を押して、ターンの終わりを待っているターン。停止のボタンを「停止中」にして、もう一度押せないようにする
  stoppingTurnId: string | null
}

export const EMPTY_SESSION_CHAT: SessionChat = {
  items: [],
  runningTurnId: null,
  generationCompletedTurnId: null,
  runningTurnStage: null,
  runningTurnWaiting: null,
  stoppingTurnId: null,
}

const replaceAt = <A>(items: readonly A[], index: number, item: A): readonly A[] =>
  items.map((current, currentIndex) => (currentIndex === index ? item : current))

// id が同じ行があれば置き換え、なければ後ろに足す
const upsertItem = (items: readonly ChatItem[], item: ChatItem): readonly ChatItem[] => {
  const index = items.findIndex(({ id }) => id === item.id)
  return index === -1 ? [...items, item] : replaceAt(items, index, item)
}

/**
 * 承認の要求を、承認を待っているツールの行の上に入れる。
 * ツールの呼び出しは始まったときに会話に入り、承認の要求はその後に届く。後ろに足すと、承認のカードより上に
 * 「KPI「…」を作成」などのツールの行が出て、承認する前に作ったように見えるためである。
 * 同じ承認がすでにあれば、答えと送っている印を残して中身だけを置き換える（会話記録から作り直すときは、答えの記録が後から届く）
 */
const upsertApproval = (session: SessionChat, turnId: string, approval: ApprovalRequest): SessionChat => {
  const existing = session.items.find((item) => item.id === approval.id)
  const item: ChatItem = {
    kind: 'approval',
    turnId,
    answer: existing?.kind === 'approval' ? existing.answer : null,
    isAnswering: existing?.kind === 'approval' && existing.isAnswering,
    ...approval,
  }
  if (existing !== undefined) {
    return { ...session, items: upsertItem(session.items, item) }
  }
  const toolIndex = session.items.findIndex(({ id }) => id === approval.toolItemId)
  return {
    ...session,
    items: toolIndex === -1 ? [...session.items, item] : session.items.toSpliced(toolIndex, 0, item),
  }
}

type ApprovalItem = Extract<ChatItem, { kind: 'approval' }>

const updateApproval = (
  session: SessionChat,
  approvalId: string,
  update: (approval: ApprovalItem) => ApprovalItem,
): SessionChat => ({
  ...session,
  items: session.items.map((item) => (item.kind === 'approval' && item.id === approvalId ? update(item) : item)),
})

const lastItemOfTurn = (session: SessionChat, turnId: string): { index: number; item: ChatItem } | null => {
  const index = session.items.findLastIndex((item) => item.turnId === turnId)
  const item = session.items.at(index)
  return index === -1 || item === undefined ? null : { index, item }
}

// 2つ目からの回答の行は id に連番を付け、React の key と重ならないようにする
const pushAnswer = (
  session: SessionChat,
  turnId: string,
  fields: { text: string; isStreaming: boolean; messageId: string | null },
): SessionChat => {
  const count = session.items.filter((item) => item.kind === 'assistant' && item.turnId === turnId).length
  const id = count === 0 ? `${turnId}:assistant` : `${turnId}:assistant:${String(count)}`
  return { ...session, items: [...session.items, { kind: 'assistant', id, turnId, ...fields }] }
}

/**
 * ツールの前後や別の agentMessage の回答を混ぜないよう、同じメッセージの連続した断片だけを同じ行に足す。
 */
const appendAnswerDelta = (
  session: SessionChat,
  turnId: string,
  delta: string,
  messageId: string | null,
): SessionChat => {
  const last = lastItemOfTurn(session, turnId)
  if (
    last?.item.kind === 'assistant' &&
    last.item.isStreaming &&
    (messageId === null || last.item.messageId === null || last.item.messageId === messageId)
  ) {
    return { ...session, items: replaceAt(session.items, last.index, { ...last.item, text: last.item.text + delta }) }
  }
  return pushAnswer(session, turnId, { text: delta, isStreaming: true, messageId })
}

/**
 * 確定した回答の全文を、ターンの最後の回答の行に映す。
 * 確定した回答は Codex の最後の agentMessage だけなので、断片で出した行がそれで終わっていれば、
 * 前に続く途中の説明を消さないよう、文はそのままにする。回答がなかったターン（空の文）では行を足さない
 */
const setAnswerText = (session: SessionChat, turnId: string, text: string): SessionChat => {
  const last = lastItemOfTurn(session, turnId)
  if (last?.item.kind !== 'assistant') {
    return text === '' ? session : pushAnswer(session, turnId, { text, isStreaming: false, messageId: null })
  }
  const answer = { ...last.item, text: last.item.text.endsWith(text) ? last.item.text : text, isStreaming: false }
  return { ...session, items: replaceAt(session.items, last.index, answer) }
}

// 回答の文を書き終えた。messageId が分からない記録では、ターンの最後の行が回答ならそれを書き終えたものにする
const finishAnswer = (session: SessionChat, turnId: string, messageId: string | null): SessionChat => {
  const lastIndex = lastItemOfTurn(session, turnId)?.index
  const isFinishedAnswer = (item: ChatItem, index: number): boolean =>
    item.kind === 'assistant' &&
    item.turnId === turnId &&
    (messageId === null ? index === lastIndex : item.messageId === messageId)
  return {
    ...session,
    items: session.items.map((item, index) =>
      item.kind === 'assistant' && isFinishedAnswer(item, index) ? { ...item, isStreaming: false } : item,
    ),
  }
}

/**
 * ターンの終わりを表示に映す。中断や失敗では、Codex が完了を知らせないまま終わったツールが残るので、ターンの終わり方で閉じる。
 * 答えないまま残った承認は、ターンが終わればもう答えられないので、取り下げられたことにしてボタンを消す
 */
const finishTurn = (
  session: SessionChat,
  turnId: string,
  end: { status: TurnEndStatus; message: string | null },
): SessionChat => {
  const { status, message } = end
  const isRunningTurn = session.runningTurnId === turnId
  const items = session.items.map((item): ChatItem => {
    if (item.turnId !== turnId) {
      return item
    }
    if (item.kind === 'assistant' && item.isStreaming) {
      return { ...item, isStreaming: false }
    }
    if (item.kind === 'approval' && item.answer === null) {
      return { ...item, answer: 'cancelled', isAnswering: false }
    }
    return item.kind === 'tool' && item.status === 'inProgress' ? { ...item, status } : item
  })
  return {
    ...session,
    items:
      status === 'completed'
        ? items
        : upsertItem(items, { kind: 'turnEnd', id: `${turnId}:end`, turnId, status, message }),
    runningTurnId: isRunningTurn ? null : session.runningTurnId,
    runningTurnStage: isRunningTurn ? null : session.runningTurnStage,
    runningTurnWaiting: isRunningTurn ? null : session.runningTurnWaiting,
    stoppingTurnId: session.stoppingTurnId === turnId ? null : session.stoppingTurnId,
  }
}

const applyChatEvent = (session: SessionChat, turnId: string, event: ChatEvent): SessionChat => {
  switch (event.kind) {
    case 'userPrompt': {
      return {
        ...session,
        items: upsertItem(session.items, { kind: 'user', id: `${turnId}:user`, turnId, text: event.text }),
      }
    }
    case 'turnProgressed': {
      // 段階は前にしか進まないので、届いた段階で置き換える
      return session.runningTurnId === turnId ? { ...session, runningTurnStage: event.stage } : session
    }
    case 'answerDelta': {
      return appendAnswerDelta(session, turnId, event.delta, event.messageId)
    }
    case 'answerFinished': {
      return finishAnswer(session, turnId, event.messageId)
    }
    case 'answerText': {
      return setAnswerText(session, turnId, event.text)
    }
    case 'toolUpdated': {
      return { ...session, items: upsertItem(session.items, { kind: 'tool', turnId, ...event.tool }) }
    }
    case 'approvalRequested': {
      return upsertApproval(session, turnId, event.approval)
    }
    case 'approvalAnswered': {
      return answerChatApproval(session, event.approvalId, event.answer)
    }
    case 'generationCompleted': {
      return { ...session, generationCompletedTurnId: turnId }
    }
    case 'turnFinished': {
      return finishTurn(session, turnId, { status: event.status, message: event.error })
    }
  }
}

/**
 * 指示を送ったターンを始める。API の応答を待たずに、指示と「考え中」を出す。
 * 会話に何もなければセッションの最初の指示で、runtime は Codex を起動するので、送った直後から「接続しています」を出す
 */
export const startChatTurn = (
  session: SessionChat,
  turn: { turnId: string; prompt: string; startedAt: string },
): SessionChat => {
  const { turnId, prompt, startedAt } = turn
  return {
    ...session,
    items: [...session.items, { kind: 'user', id: `${turnId}:user`, turnId, text: prompt }],
    runningTurnId: turnId,
    runningTurnStage: null,
    runningTurnWaiting: { sentAt: startedAt, isFirstTurn: session.items.length === 0 },
  }
}

// 仮の turnId で表示していた行を、API が振った turnId（X-Turn-Id）に付け替える。
// 指示と回答の行の id は `{turnId}:…` の形なので付け替える。ツールと承認の行の id は runtime が振ったものなので変えない
export const renameChatTurn = (session: SessionChat, pendingTurnId: string, turnId: string): SessionChat => {
  const rename = (id: string | null): string | null => (id === pendingTurnId ? turnId : id)
  const renameItemId = (id: string): string =>
    id.startsWith(`${pendingTurnId}:`) ? `${turnId}${id.slice(pendingTurnId.length)}` : id
  return {
    ...session,
    items: session.items.map((item) =>
      item.turnId === pendingTurnId ? { ...item, turnId, id: renameItemId(item.id) } : item,
    ),
    runningTurnId: rename(session.runningTurnId),
    generationCompletedTurnId: rename(session.generationCompletedTurnId),
    stoppingTurnId: rename(session.stoppingTurnId),
  }
}

// 届いた出来事を順に会話に映す
export const applyChatEvents = (session: SessionChat, turnId: string, events: readonly ChatEvent[]): SessionChat => {
  const applied = events.reduce((current, event) => applyChatEvent(current, turnId, event), session)
  // 実行中のターンに runtime のイベントが届いたので、最初のイベントを待つのを終える
  return events.length > 0 && session.runningTurnId === turnId ? { ...applied, runningTurnWaiting: null } : applied
}

// 実行中のターンの停止を押した。ターンの終わりが届くまで、停止のボタンを「停止中」にする
export const requestChatTurnStop = (session: SessionChat, turnId: string): SessionChat =>
  session.runningTurnId === turnId ? { ...session, stoppingTurnId: turnId } : session

// 停止の要求が失敗した。ターンは続いているので、もう一度押せるように停止のボタンに戻す
export const clearChatTurnStop = (session: SessionChat, turnId: string): SessionChat =>
  session.stoppingTurnId === turnId ? { ...session, stoppingTurnId: null } : session

// 会話記録からターンを作り直す前に、そのターンの表示を消して実行中にする（つなぎ直し）
export const resetChatTurn = (session: SessionChat, turnId: string): SessionChat => ({
  ...session,
  items: session.items.filter((item) => item.turnId !== turnId),
  runningTurnId: turnId,
  // 途中からの配信には progress が届かないので、段階を持たずに「考え中」を出す
  runningTurnStage: null,
  runningTurnWaiting: null,
})

// 配信が終わったのにターンの終わりを受けていないとき（送れなかった、切れた）に、実行中を解いて理由を出す
export const closeChatTurnStream = (session: SessionChat, turnId: string, message: string): SessionChat =>
  session.runningTurnId === turnId ? finishTurn(session, turnId, { status: 'failed', message }) : session

// 承認のカードで答えを送った。API の応答を待つ間は、同じ承認に答えられなくする
export const startChatApprovalAnswer = (session: SessionChat, approvalId: string): SessionChat =>
  updateApproval(session, approvalId, (approval) => ({ ...approval, isAnswering: true }))

// 答えを送れなかった。答えられる状態に戻す
export const failChatApprovalAnswer = (session: SessionChat, approvalId: string): SessionChat =>
  updateApproval(session, approvalId, (approval) => ({ ...approval, isAnswering: false }))

// 送った答えを API が受け付けなかった（期限切れ、回答済み、runtime がそのターンを実行していない）。
// 送り直しても受け付けられないので、答えられなくする
export const rejectChatApprovalAnswer = (session: SessionChat, approvalId: string): SessionChat =>
  updateApproval(session, approvalId, (approval) => ({ ...approval, answer: 'notAccepted', isAnswering: false }))

// 承認に答えた。回答の記録（approval.answered）は会話記録から作り直すときにしか届かないので、このタブで答えたときは API が受け付けた時点で映す
export const answerChatApproval = (session: SessionChat, approvalId: string, answer: ApprovalAnswer): SessionChat =>
  updateApproval(session, approvalId, (approval) => ({ ...approval, answer, isAnswering: false }))

/**
 * その承認に答えを送れるか。まだ結末が決まっておらず、このタブで答えを送っている最中でもないときだけ
 */
export const isApprovalAnswerable = (session: SessionChat | undefined, approvalId: string): boolean => {
  const approval = session?.items.find((item) => item.kind === 'approval' && item.id === approvalId)
  return approval?.kind === 'approval' && approval.answer === null && !approval.isAnswering
}

/**
 * 「考え中」を出すか。実行中のターンで、エージェントがまだ回答を終えておらず、回答の文を書いている途中でもないとき。
 * 回答の文を書いている間は、文が増えていくことで実行中だと分かるので出さない
 */
export const isThinking = (session: SessionChat): boolean => {
  const { runningTurnId, generationCompletedTurnId } = session
  const last = session.items.at(-1)
  const isWritingAnswer = last?.kind === 'assistant' && last.isStreaming && last.text !== ''
  return runningTurnId !== null && generationCompletedTurnId !== runningTurnId && !isWritingAnswer
}
